/**
 * Format a timestamp to a localized date/time string
 */
export function formatDateTime(timestamp: string, lang = 'en-US'): string {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString(lang, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Placeholder shown when a video clip duration is missing or invalid.
 */
export const EMPTY_VIDEO_DURATION = '--:--';

/**
 * Formats a video clip duration in seconds for thumbnail badges.
 * Returns `HH:MM:SS` when hours are present, otherwise `MM:SS`.
 * Seconds are floored; returns `null` when the value is missing or invalid.
 */
export function formatVideoDurationSeconds(durationSec: number | null | undefined): string | null {
    if (durationSec == null || !Number.isFinite(durationSec) || durationSec < 0) {
        return null;
    }

    const totalSeconds = Math.floor(durationSec);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const paddedMinutes = minutes.toString().padStart(2, '0');
    const paddedSeconds = seconds.toString().padStart(2, '0');

    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${paddedMinutes}:${paddedSeconds}`;
    }

    return `${paddedMinutes}:${paddedSeconds}`;
}

/**
 * Like {@link formatVideoDurationSeconds}, but returns {@link EMPTY_VIDEO_DURATION} when the value is missing or invalid.
 */
export function formatVideoDurationDisplay(durationSec: number | null | undefined): string {
    return formatVideoDurationSeconds(durationSec) ?? EMPTY_VIDEO_DURATION;
}

/**
 * Formats a video clip duration for screen readers (e.g. "1 minute 30 seconds").
 * Returns `null` when the value is missing or invalid.
 */
export function formatVideoDurationAccessibleDescription(durationSec: number | null | undefined): string | null {
    if (durationSec == null || !Number.isFinite(durationSec) || durationSec < 0) {
        return null;
    }

    const totalSeconds = Math.floor(durationSec);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];

    if (hours > 0) {
        parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    }

    if (minutes > 0) {
        parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    }

    if (seconds > 0 || parts.length === 0) {
        parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
    }

    return parts.join(' ');
}

/**
 * Calculate duration in seconds between two timestamps.
 */
export function calculateDurationSeconds(startedAt: string | Date, completedAt: string | Date): number {
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();

    return Math.floor((end - start) / 1000);
}

/**
 * Calculate whole days elapsed since a timestamp.
 */
export function daysSince(dateString: string): number {
    return Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Formats a date/time as "MMM DD, h:mm AM/PM"
 */
export function formatDateShort(isoTimestamp: string, locale = 'en-US'): string {
    const date = new Date(isoTimestamp);
    const month = date.toLocaleString(locale, { month: 'short' });
    const day = date.getDate();
    const time = date.toLocaleString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

    return `${month} ${day}, ${time}`;
}

/**
 * Formats a duration between two timestamps as "Xh Ym" or "Ym"
 */
export function formatDuration(startedAt: string, completedAt: string): string {
    const durationSeconds = calculateDurationSeconds(startedAt, completedAt);

    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
}

/**
 * Calculate duration in minutes between two timestamps.
 */
export function calculateDurationMinutes(startedAt: string | Date, completedAt: string | Date): number {
    return Math.floor(calculateDurationSeconds(startedAt, completedAt) / 60);
}

/**
 * Format duration in milliseconds to human-readable format (e.g., "2h 30min", "45min", "30s")
 */
export const formatDurationHumanReadable = (durationMs: number): string => {
    const totalSeconds = Math.floor(durationMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    if (totalMinutes === 0) {
        return `${seconds}s`;
    }

    return `${hours ? `${hours}h` : ''} ${minutes ? `${minutes}min` : ''}`.trim();
};
